import { run } from "./registry";
import { initToasts, toast } from "./toast";
import { focusField } from "./keyboard";
import "./actions";
import "./shell";
import "./auth";
import "./hotkeys";
import "./dirty";
import "./miti-input";
import "./autocomplete";
import "./dup-check";
import "./entry-grid";
import "./print-prompt";
import "./palette";
import "./find-doc";
import "./list";
import "./report-filters";
import "./truck-arrival";
import "./pod";
import "./adjustment-note";
import "./journal-voucher";
import "./pdc-pick";
import "./draft";
import "./vcts";
import "./bilti-entry";
import "./template-editor";
import "./template-designer";
import "./charge-switch";
import "./quick-create";
import "./unique-col";
import "./show-when";
import "./row-default";
import "./money-receipt";
import "./bilti-vat";
import "./dashboard";
import "./bell";

declare global {
  interface Window {
    dhuwani: { toast: typeof toast };
  }
}

window.dhuwani = { toast };

document.addEventListener("DOMContentLoaded", () => {
  initToasts();
  const pending = sessionStorage.getItem("dhuwani-toast");
  if (pending) {
    sessionStorage.removeItem("dhuwani-toast");
    document.body.dispatchEvent(new CustomEvent("toast", { detail: JSON.parse(pending) }));
  }
  const pendingToasts = sessionStorage.getItem("dhuwani-toasts");
  if (pendingToasts) {
    sessionStorage.removeItem("dhuwani-toasts");
    try {
      const arr = JSON.parse(pendingToasts);
      if (Array.isArray(arr)) {
        for (const t of arr) {
          document.body.dispatchEvent(new CustomEvent("toast", { detail: t }));
        }
      }
    } catch { /* ignored */ }
  }
  const saved = document.getElementById("place-saved-toast")?.getAttribute("data-place-saved");
  if (saved) toast("ok", saved);
  run(document);
  function handleTrigger(raw: string | null | undefined) {
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (payload["kit:focus"]) {
        const target = document.querySelector<HTMLElement>(payload["kit:focus"]);
        if (target) {
          focusField(target);
        }
      }
      // "kit:fill": {selector: value} sets fields in place (e.g. a truck's usual driver) without replacing them, so focus
      // and keyboard order keep working; a hidden id that declares hx-trigger also hears the change.
      const fill = payload["kit:fill"] as Record<string, string> | undefined;
      if (fill) {
        for (const [selector, value] of Object.entries(fill)) {
          const field = document.querySelector<HTMLInputElement>(selector);
          if (!field) continue;
          field.value = value;
          if (field.type === "hidden" && field.hasAttribute("hx-trigger")) field.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      // htmx already fires every HX-Trigger key as its own event ("toast", "printPrompt", "confirm", "kit:reset"),
      // so only the kit:* aliases are re-dispatched here; re-firing the native names ran each handler twice (double save).
      const toastItem = payload.toast || payload["kit:toast"];
      if (toastItem) {
        try { sessionStorage.setItem("dhuwani-toast", JSON.stringify(toastItem)); } catch { /* storage unavailable */ }
        if (!payload.toast) document.body.dispatchEvent(new CustomEvent("toast", { detail: toastItem }));
      }
      if (Array.isArray(payload.toasts)) {
        try { sessionStorage.setItem("dhuwani-toasts", JSON.stringify(payload.toasts)); } catch { /* storage unavailable */ }
        for (const t of payload.toasts) {
          document.body.dispatchEvent(new CustomEvent("toast", { detail: t }));
        }
      }
      if (!payload.printPrompt && payload["kit:print"]) {
        document.body.dispatchEvent(new CustomEvent("printPrompt", { detail: payload["kit:print"] }));
      }
      if (payload.kitReset && !payload["kit:reset"]) {
        document.body.dispatchEvent(new CustomEvent("kit:reset"));
      }
    } catch { /* malformed server trigger is ignored */ }
  }

  document.body.addEventListener("htmx:afterRequest", (event: Event) => {
    const detail = (event as CustomEvent).detail;
    handleTrigger(detail?.xhr?.getResponseHeader?.("HX-Trigger"));
    handleTrigger(detail?.xhr?.getResponseHeader?.("HX-Trigger-After-Settle"));
    handleTrigger(detail?.xhr?.getResponseHeader?.("HX-Trigger-After-Swap"));
  });
  document.body.addEventListener("htmx:afterSettle", (event: Event) => {
    const detail = (event as CustomEvent).detail;
    handleTrigger(detail?.xhr?.getResponseHeader?.("HX-Trigger-After-Settle"));
  });
  document.body.addEventListener("kit:init", (event: Event) => run((event as CustomEvent).detail as ParentNode));
  // detail.target is the swapped element; e.target can be the element that made the request.
  document.body.addEventListener("htmx:afterSwap", (e: Event) => {
    const swapped = ((e as CustomEvent).detail?.target as Element) ?? (e.target as Element) ?? document;
    run(swapped);
    // A swap that removed the focused field (Against → new allocation grid) leaves focus on <body>: carry on in the new content.
    // After run(), so the grid has built its rows.
    if (swapped instanceof HTMLElement && (!document.activeElement || document.activeElement === document.body)) {
      // Entry-grid cells only: a list swap (POD Show) leaves its row focus to list.ts.
      const candidates = swapped.querySelectorAll<HTMLElement>("table[data-kit-grid] tbody tr [data-kit-col]:not([readonly]):not([disabled]):not([type=hidden])");
      for (const c of Array.from(candidates)) {
        c.focus();
        if (document.activeElement === c) { if (c instanceof HTMLInputElement) c.select(); break; }
      }
    }
  });
});
